import { h } from 'vue'
import { useI18n } from 'vue-i18n'
import { toast } from '~/components/ui/toast'

interface ReverbMessage {
  title: Record<string, string>
  message: Record<string, string>
  type?: 'credit'
}

let reverbInstance: WebSocket | null = null
let reconnectTimer: ReturnType<typeof setTimeout> | null = null
let reconnectAttempts = 0
let isReconnecting = false
const maxReconnectAttempts = 5

function getReconnectDelay(attempt: number): number {
  return Math.min(1000 * Math.pow(2, attempt), 30000)
}

function closeInstance() {
  if (reverbInstance) {
    reverbInstance.onopen = null
    reverbInstance.onmessage = null
    reverbInstance.onclose = null
    reverbInstance.onerror = null
    reverbInstance.close()
    reverbInstance = null
  }
}

export const useReverb = () => {
  const config = useRuntimeConfig()
  const { t, locale } = useI18n()

  const initializeReverb = () => {
    if (reverbInstance && (reverbInstance.readyState === WebSocket.OPEN || reverbInstance.readyState === WebSocket.CONNECTING)) {
      return reverbInstance
    }

    closeInstance()

    const reverbUrl = config.public.reverbUrl || 'wss://ws.banks.kg'
    const reverbAppKey = config.public.reverbAppKey || ''

    if (!reverbAppKey) {
      if (import.meta.dev) {
        console.warn('[useReverb] reverbAppKey is not configured, skipping WebSocket init')
      }
      return null
    }

    try {
      const wsUrl = `${reverbUrl}/app/${reverbAppKey}?protocol=7&client=js&version=8.4.0-rc2&flash=false`
      reverbInstance = new WebSocket(wsUrl)

      reverbInstance.onopen = () => {
        reconnectAttempts = 0
        isReconnecting = false

        if (reverbInstance?.readyState === WebSocket.OPEN) {
          reverbInstance.send(JSON.stringify({
            event: 'pusher:subscribe',
            data: { channel: 'public-channel', auth: '' }
          }))
        }
      }

      reverbInstance.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data)

          if (data.event === 'pusher:ping') {
            if (reverbInstance?.readyState === WebSocket.OPEN) {
              reverbInstance.send(JSON.stringify({ event: 'pusher:pong', data: '{}' }))
            }
            return
          }

          if (data.event?.startsWith('pusher:') || data.event?.startsWith('pusher_internal:')) {
            return
          }

          if (!data.event || !data.data) return

          const messageData: ReverbMessage =
            typeof data.data === 'string' ? JSON.parse(data.data) : data.data

          const prefix = locale.value === 'ru' ? '' : `/${locale.value}`
          const title = messageData.title[locale.value] || t('common.new_message')
          const message = messageData.message[locale.value]

          if (messageData.type === 'credit') {
            toast({
              title,
              description: h('div', [
                h('div', message),
                h('div', { class: 'mt-2' }, [
                  h('a', {
                    href: `${prefix}/credits/online-request`,
                    class: 'text-base font-semibold text-blue-600 hover:underline'
                  }, t('common.i_also_want_a_loan'))
                ])
              ]),
              variant: 'default'
            })
          } else {
            toast({ title, description: message })
          }
        } catch (error) {
          if (import.meta.dev) {
            console.warn('[useReverb] Failed to parse message:', error)
          }
        }
      }

      reverbInstance.onclose = () => {
        if (isReconnecting) return

        isReconnecting = true
        if (reconnectTimer) clearTimeout(reconnectTimer)

        if (reconnectAttempts >= maxReconnectAttempts) {
          reconnectAttempts = maxReconnectAttempts
        }

        reconnectTimer = setTimeout(() => {
          reconnectAttempts++
          isReconnecting = false
          initializeReverb()
        }, getReconnectDelay(reconnectAttempts))
      }

      reverbInstance.onerror = () => {}

      return reverbInstance
    } catch (error) {
      if (import.meta.dev) {
        console.error('[useReverb] Failed to initialize WebSocket:', error)
      }
      return null
    }
  }

  const disconnect = () => {
    if (reconnectTimer) {
      clearTimeout(reconnectTimer)
      reconnectTimer = null
    }

    isReconnecting = false
    reconnectAttempts = 0
    closeInstance()
  }

  return {
    initializeReverb,
    disconnect
  }
}
